import { call, takeEvery, put, select, takeLatest } from 'redux-saga/effects'
import { keyBy } from 'lodash'

import StadiumRemote from '../remote/stadiumRemote'

import { PRINT_ORDER_REQUESTED, FETCH_PRINTERS_REQUESTED, fetchPrintersSucceeded, fetchPrintersFailed } from '../actions/printer'

import { makeGetCurrentMenu } from '../selectors/menus'
import { getDeviceSubtype } from '../selectors/config'
import { getNetworkPrinter } from '../selectors/printer'
import { getOrderInProgress, makeGetOrder } from '../selectors/order'
import { makeGetServiceChargeLegalText, getVenueName } from '../selectors/venue'

import createPrinterData, { DEFAULT_PRINTERS, getPrintCount, PRINT_TYPES } from '../utils/printing'
import PeripheralBridge from '../utils/peripheralBridge'
import createDataDogLog from '../utils/datadog'

import MutationApi from '../remote/mutationApi'
import { getActiveAttendantId, getAttendantName } from '../selectors/attendant'
import { createOrderParams } from '../utils/orderUtils'
import { ToastManager } from '../utils/toastManager'
import { bridge_sendPrintStatus } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { setRouteAction } from '../VNRoutes/Actions'

/**
 * Cloud Printing is done via a "print" order mutatation
 * we define the number of prints and template of receipt using "printType"
 *
 * @param {object} params
 * @returns {void}
 */
const printToCloud = function* (params, redirectionRoute) {
  try {
    const result = yield call(MutationApi.printOrder, params)
    const currentMutation = result?.data?.orderMutations?.[0]

    if (!currentMutation?.applied) {
      throw new Error(currentMutation?.error_message)
    }

    ToastManager.info('Receipt print sent to printer.')
  } catch (err) {
    createDataDogLog('error', `CLOUD PRINT ERROR: ${err.message ?? 'error printing receipt'}`, { params })
    bridge_sendPrintStatus(true, 'There was an error printing the receipt.')
    ToastManager.error('There was an error printing the receipt.')
  }

  if (redirectionRoute) {
    yield put(setRouteAction(redirectionRoute))
  }
}

/**
 * USB Printing is done via Android bridge call "printReceipt"
 * For printing multiple of the same auth, we must call it multiple times
 *
 * @param {object} param
 * @param {object} [params.order] - entire order payload
 * @param {string} [params.orderUuid]
 * @param {string} [params.deviceType] - 'concession' or 'kiosk'
 * @param {string} [params.printType] - type of receipt
 * @returns {void}
 */
const printToUSB = function* ({ order, orderUuid, deviceType, printType, redirectionRoute }) {
  const getServiceChargeLegalText = makeGetServiceChargeLegalText()

  const menu = yield select((state) => makeGetCurrentMenu()(state, order.standMenuUuid))
  const serviceChargeLegal = yield select((state) => getServiceChargeLegalText(state, orderUuid))
  const deviceSubtype = yield select(getDeviceSubtype)
  const venueName = yield select(getVenueName)
  const employeeId = createOrderParams(order)?.employeeId ?? order?.employeeId
  const activeAttendantId = yield select(getActiveAttendantId)
  const attendantName = yield select((state) => getAttendantName(state, employeeId ?? activeAttendantId))

  const printingData = createPrinterData({
    deviceSubtype,
    menu,
    order,
    serviceChargeLegal,
    venueName,
    attendantName,
    receiptType: printType,
  })
  const bridgeInstance = PeripheralBridge.getBridge()

  if (!bridgeInstance) {
    if (deviceType !== 'kiosk') {
      bridge_sendPrintStatus(true, 'Print Failed: Communication could not be established')
      ToastManager.error('Print Failed: Communication could not be established')
      createDataDogLog('error', 'Print Failed: Communication could not be established')
    }

    if (redirectionRoute) {
      yield put(setRouteAction(redirectionRoute))
    }
    return
  }

  for (let i = 0; i < getPrintCount(printType); i++) {
    bridgeInstance.callHandler('printReceipt', printingData)
  }

  if (redirectionRoute) {
    yield put(setRouteAction(redirectionRoute))
  }
}

/**
 * Print Order Saga
 *
 * @function
 * @param {object} [params={}]
 * @param {string} [params.payload.orderUuid]
 * @param {string} [params.payload.deviceType] - 'concession'(default) or 'kiosk'
 * @param {boolean} [params.payload.isNewOrder] - where order is in state
 * @param {string} [params.payload.printType] - type of receipt
 * @yields {void}
 */
export function* printOrderSaga(params = {}) {
  const orderUuid = params?.payload?.orderUuid
  const deviceType = params?.payload?.deviceType ?? 'concessions'
  const isNewOrder = params?.payload?.isNewOrder
  const redirectionRoute = params?.payload?.redirectionRoute
  const printType = params?.payload?.printType ?? PRINT_TYPES.RECEIPT
  const currentNetworkPrinter = yield select((state) => getNetworkPrinter(state))

  if (!orderUuid) {
    bridge_sendPrintStatus(true, 'Print Failed')
    ToastManager.error('Required: orderUuid is missing')
    createDataDogLog('error', 'Print Failed :Required: orderUuid is missing')
    return
  }

  // Cloud Printing
  if (currentNetworkPrinter && currentNetworkPrinter !== DEFAULT_PRINTERS.USB.uuid && deviceType !== 'kiosk') {
    yield printToCloud(
      {
        orderUuid,
        printType,
        printerUuids: [currentNetworkPrinter],
        paymentUuid: null,
      },
      redirectionRoute,
    )
    return
  }

  // USB Printing
  const getOrder = makeGetOrder()
  const order = isNewOrder
    ? yield select((state) => getOrderInProgress(state))
    : yield select((state) => getOrder(state, orderUuid))

  yield printToUSB({ order, orderUuid, deviceType, printType, redirectionRoute })
}

/**
 * @deprecated Marked for deletion. Not used.
 * "getPrinters" is now a selector and does not exist in "Remote"
 */
export function* fetchPrinters(params = {}) {
  try {
    const result = yield call(StadiumRemote.fetchPrinters)
    const byId = keyBy(result?.data?.printers, 'uuid')
    yield put(fetchPrintersSucceeded({ byId }))
  } catch (err) {
    yield put(fetchPrintersFailed(err))
  }
}

export function* watchPrintOrder() {
  yield takeEvery(PRINT_ORDER_REQUESTED, printOrderSaga)
}

export function* watchFetchPrinters() {
  yield takeLatest(FETCH_PRINTERS_REQUESTED, fetchPrinters)
}
